export interface WorldPoint {
  x: number;
  y: number;
}

export interface GridPoint {
  col: number;
  row: number;
}

export class IsoGrid {
  readonly cols: number;
  readonly rows: number;
  readonly tileWidth: number;
  readonly tileHeight: number;

  private readonly halfW: number;
  private readonly halfH: number;
  private cells: Map<string, string>;

  constructor(cols: number, rows: number, tileWidth: number, tileHeight: number) {
    this.cols = cols;
    this.rows = rows;
    this.tileWidth = tileWidth;
    this.tileHeight = tileHeight;
    this.halfW = tileWidth / 2;
    this.halfH = tileHeight / 2;
    this.cells = new Map();
  }

  /** Convert grid coordinates to world (pixel) position at tile center */
  gridToWorld(col: number, row: number): WorldPoint {
    return {
      x: (col - row) * this.halfW,
      y: (col + row) * this.halfH,
    };
  }

  /** Convert world (pixel) position to grid coordinates */
  worldToGrid(worldX: number, worldY: number): GridPoint {
    const col = (worldX / this.halfW + worldY / this.halfH) / 2;
    const row = (worldY / this.halfH - worldX / this.halfW) / 2;
    return {
      col: Math.round(col),
      row: Math.round(row),
    };
  }

  /** Check if grid coordinates are within bounds */
  isInBounds(col: number, row: number): boolean {
    return col >= 0 && col < this.cols && row >= 0 && row < this.rows;
  }

  /** Check if a cell is occupied by an object */
  isCellOccupied(col: number, row: number): boolean {
    return this.cells.has(this.cellKey(col, row));
  }

  /** Get the content ID of a cell, or null if empty */
  getCellContent(col: number, row: number): string | null {
    return this.cells.get(this.cellKey(col, row)) ?? null;
  }

  /** Mark a cell as occupied with an object ID */
  setCellOccupied(col: number, row: number, objectId: string): void {
    this.cells.set(this.cellKey(col, row), objectId);
  }

  /** Clear a cell's occupancy */
  clearCell(col: number, row: number): void {
    this.cells.delete(this.cellKey(col, row));
  }

  private cellKey(col: number, row: number): string {
    return `${col},${row}`;
  }
}
